import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuthStore, type UserRole } from '@/stores/authStore';
import { DataSourceBanner } from '@/components/DataSourceBanner';
import { SkeletonGrid } from '@/components/feedback/Skeleton';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: '▦' },
  { to: '/leads', label: 'Leads', icon: '◈' },
  { to: '/clients', label: 'Clientes', icon: '◉' },
  { to: '/agenda', label: 'Agenda', icon: '▤' },
  { to: '/agenda/settings', label: 'Horario agenda', icon: '◔', adminOnly: true },
  { to: '/payments', label: 'Pagos', icon: '◎', adminOnly: true },
  // UC-083 — junto a Pagos, que es su mismo dominio.
  { to: '/plans', label: 'Planes', icon: '⬡', adminOnly: true },
  { to: '/monitor', label: 'Monitor n8n', icon: '◇' },
  { to: '/settings', label: 'Ajustes', icon: '◐', adminOnly: true },
];

function visibleItems(role: UserRole | null): NavItem[] {
  if (role === 'admin') return navItems;
  return navItems.filter((item) => !item.adminOnly);
}

function roleBadge(role: UserRole | null) {
  if (role === 'admin') {
    return (
      <span className="ml-2 rounded-full bg-gn-mint px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gn-primary-forest">
        admin
      </span>
    );
  }
  if (role === 'operator') {
    return (
      <span className="ml-2 rounded-full bg-gn-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gn-ink/70">
        operador
      </span>
    );
  }
  return null;
}

/**
 * El menu, una sola vez. Lo usan el lateral de escritorio y el cajon del movil:
 * si estuviera escrito dos veces, tarde o temprano un enlace nuevo aparaceria
 * solo en uno de los dos y nadie se enteraria hasta que alguien lo buscase desde
 * el telefono.
 *
 * `py-3` deja cada enlace en 44 px de alto, el minimo tactil que pide AC-56.
 */
function Menu({ role, onNavegar }: { role: UserRole | null; onNavegar?: () => void }) {
  return (
    <nav className="flex-1 space-y-1" aria-label="Secciones del panel">
      {visibleItems(role).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavegar}
          className={({ isActive }) =>
            [
              'flex min-h-[44px] items-center gap-3 rounded-full px-4 py-3 text-sm font-medium transition-colors',
              isActive
                ? 'bg-gn-mint text-gn-primary-forest'
                : 'text-gn-ink/70 hover:bg-gn-bg hover:text-gn-primary-forest',
            ].join(' ')
          }
        >
          <span aria-hidden className="text-base">
            {item.icon}
          </span>
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Sesion({
  email,
  role,
  onSalir,
}: {
  email: string | undefined;
  role: UserRole | null;
  onSalir: () => void;
}) {
  return (
    <div className="mt-6 border-t border-gn-border pt-4">
      <div className="mb-3 px-2">
        <p className="flex items-center text-xs uppercase tracking-wider text-gn-ink-muted">
          Sesión
          {roleBadge(role)}
        </p>
        <p className="truncate text-sm font-medium text-gn-ink">{email ?? '—'}</p>
      </div>
      <button
        onClick={onSalir}
        className="min-h-[44px] w-full rounded-full border border-gn-border px-4 py-2 text-sm font-medium text-gn-ink/70 hover:bg-gn-bg"
      >
        Cerrar sesión
      </button>
    </div>
  );
}

export function AppLayout() {
  const { user, role, signOut } = useAuthStore();
  const [cajonAbierto, setCajonAbierto] = useState(false);
  const location = useLocation();
  const botonMenu = useRef<HTMLButtonElement>(null);
  const botonCerrar = useRef<HTMLButtonElement>(null);

  /*
    EN EL MOVIL NO HABIA MANERA DE NAVEGAR. UC-047 / AC-56.

    El lateral es `hidden md:flex`, asi que por debajo de 768 px desaparecia y no
    habia nada en su lugar: quien entraba desde el telefono se quedaba encerrado
    en la pantalla que le tocara. Y el AC dice justamente que el panel se usara
    sobre todo desde el movil.
  */

  // Al cambiar de pantalla el cajon se cierra solo. Si no, tapa lo que acabas
  // de pedir.
  useEffect(() => {
    setCajonAbierto(false);
  }, [location.pathname]);

  // Escape cierra, y mientras esta abierto el fondo no se desplaza.
  useEffect(() => {
    if (!cajonAbierto) return;
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCajonAbierto(false);
    };
    document.addEventListener('keydown', alPulsar);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    botonCerrar.current?.focus();
    return () => {
      document.removeEventListener('keydown', alPulsar);
      document.body.style.overflow = overflowPrevio;
    };
  }, [cajonAbierto]);

  const cerrarCajon = () => {
    setCajonAbierto(false);
    // El foco vuelve de donde salio, no al principio del documento.
    botonMenu.current?.focus();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gn-bg">
      <DataSourceBanner />
      <div className="flex flex-1">
        <aside className="hidden w-64 flex-col border-r border-gn-border bg-gn-white p-5 md:flex">
          <div className="mb-8 px-2">
            <h1 className="font-headline text-2xl font-bold text-gn-primary-forest">Green Panel</h1>
            <p className="mt-1 text-xs uppercase tracking-wider text-gn-ink-muted">
              Gestión · Nutrición
            </p>
          </div>
          <Menu role={role} />
          <Sesion email={user?.email} role={role} onSalir={() => void signOut()} />
        </aside>

        {/* Cajon de navegacion del movil */}
        {cajonAbierto && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div
              className="absolute inset-0 bg-gn-ink/40"
              onClick={cerrarCajon}
              aria-hidden="true"
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Menú de navegación"
              className="absolute inset-y-0 left-0 flex w-72 max-w-[85%] flex-col overflow-y-auto border-r border-gn-border bg-gn-white p-5"
            >
              <div className="mb-6 flex items-start justify-between">
                <div>
                  <h2 className="font-headline text-xl font-bold text-gn-primary-forest">
                    Green Panel
                  </h2>
                  <p className="mt-1 text-xs uppercase tracking-wider text-gn-ink-muted">
                    Gestión · Nutrición
                  </p>
                </div>
                <button
                  ref={botonCerrar}
                  onClick={cerrarCajon}
                  aria-label="Cerrar el menú"
                  className="-mr-2 -mt-1 flex h-11 w-11 items-center justify-center rounded-full text-xl text-gn-ink/70 hover:bg-gn-bg"
                >
                  ✕
                </button>
              </div>
              <Menu role={role} onNavegar={() => setCajonAbierto(false)} />
              <Sesion email={user?.email} role={role} onSalir={() => void signOut()} />
            </div>
          </div>
        )}

        <main className="min-w-0 flex-1 overflow-x-hidden">
          <header className="flex items-center gap-3 border-b border-gn-border bg-gn-white px-4 py-3 md:hidden">
            <button
              ref={botonMenu}
              onClick={() => setCajonAbierto(true)}
              aria-label="Abrir el menú de navegación"
              aria-expanded={cajonAbierto}
              className="flex h-11 w-11 items-center justify-center rounded-full text-xl text-gn-primary-forest hover:bg-gn-bg"
            >
              ☰
            </button>
            <h1 className="font-headline text-xl font-bold text-gn-primary-forest">Green Panel</h1>
          </header>
          <div className="p-4 md:p-8">
            {/*
              Las paginas del panel se cargan por separado (UC-046/AC-55). La
              espera se pone AQUI, dentro del layout, y no arriba envolviendo las
              rutas: asi el menu lateral y la cabecera se quedan quietos mientras
              llega la pantalla, en vez de desaparecer y volver en cada navegacion.
            */}
            <Suspense fallback={<SkeletonGrid count={4} variant="card" />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
